// ╔══════════════════════════════════════════════════════════════════╗
// ║                    ADMIN LOANS PAGE                              ║
// ╚══════════════════════════════════════════════════════════════════╝

import React, { useState, useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import {
  Landmark,
  Search,
  Filter,
  ChevronLeft,
  ChevronRight,
  Eye,
  CheckCircle,
  XCircle,
  Loader2,
  X,
  User,
  Calendar,
  DollarSign,
  Percent,
  Clock,
  AlertCircle,
  FileText,
  RefreshCw,
  Plus,
  Edit2,
  Trash2,
  Save
} from 'lucide-react'
import { LoansAdminAPI } from '../../services/adminApi'
import { useAdmin } from '../AdminApp'

export default function LoansPage() {
  const { hasPermission } = useAdmin()
  const [activeTab, setActiveTab] = useState('applications')

  // Applications state
  const [applications, setApplications] = useState([])
  const [loadingApps, setLoadingApps] = useState(true)

  // Products state
  const [products, setProducts] = useState([])
  const [loadingProducts, setLoadingProducts] = useState(false)

  // Common state
  const [error, setError] = useState(null)
  const [search, setSearch] = useState('')
  const [statusFilter, setStatusFilter] = useState('')
  const [page, setPage] = useState(1)
  const [totalPages, setTotalPages] = useState(1)
  const [total, setTotal] = useState(0)

  // Modals
  const [selectedLoan, setSelectedLoan] = useState(null)
  const [showDetailModal, setShowDetailModal] = useState(false)
  const [showProductModal, setShowProductModal] = useState(false)
  const [editingProduct, setEditingProduct] = useState(null)

  // Action states
  const [processing, setProcessing] = useState(false)
  const [rejectionReason, setRejectionReason] = useState('')

  useEffect(() => {
    if (activeTab === 'applications') {
      fetchApplications()
    } else {
      fetchProducts()
    }
  }, [activeTab, page, search, statusFilter])

  const fetchApplications = async () => {
    setLoadingApps(true)
    setError(null)

    try {
      const params = { page, limit: 20 }
      if (search) params.search = search
      if (statusFilter) params.status = statusFilter

      console.log('Fetching loans with params:', params)
      const res = await LoansAdminAPI.getLoans(params)
      console.log('Loans response:', res)

      if (res.success) {
        setApplications(res.data.loans)
        setTotalPages(res.data.pagination.pages)
        setTotal(res.data.pagination.total)
      } else {
        console.error('Loans API error:', res)
        setError(res.message || 'Unknown error')
      }
    } catch (err) {
      console.error('Loans fetch exception:', err)
      setError(`Failed to load loan applications: ${err.message}`)
    } finally {
      setLoadingApps(false)
    }
  }

  const fetchProducts = async () => {
    setLoadingProducts(true)
    setError(null)

    try {
      console.log('Fetching loan products...')
      const res = await LoansAdminAPI.getLoanProducts()
      console.log('Loan products response:', res)

      if (res.success) {
        setProducts(res.data.products)
      } else {
        console.error('Loan products API error:', res)
        setError(res.message || 'Unknown error')
      }
    } catch (err) {
      console.error('Loan products fetch exception:', err)
      setError(`Failed to load loan products: ${err.message}`)
    } finally {
      setLoadingProducts(false)
    }
  }

  const handleViewLoan = async (loan) => {
    try {
      const res = await LoansAdminAPI.getLoan(loan.id)
      if (res.success) {
        setSelectedLoan(res.data.loan)
        setShowDetailModal(true)
      }
    } catch (err) {
      console.error('Failed to load loan details')
    }
  }

  const handleApproveLoan = async (loanId) => {
    if (!confirm('Are you sure you want to approve this loan application?')) return

    setProcessing(true)
    try {
      const res = await LoansAdminAPI.approveLoan(loanId)
      if (res.success) {
        fetchApplications()
        setShowDetailModal(false)
      }
    } catch (err) {
      console.error('Failed to approve loan')
    } finally {
      setProcessing(false)
    }
  }

  const handleRejectLoan = async (loanId) => {
    if (!rejectionReason) {
      alert('Please provide a rejection reason')
      return
    }

    setProcessing(true)
    try {
      const res = await LoansAdminAPI.rejectLoan(loanId, rejectionReason)
      if (res.success) {
        fetchApplications()
        setShowDetailModal(false)
        setRejectionReason('')
      }
    } catch (err) {
      console.error('Failed to reject loan')
    } finally {
      setProcessing(false)
    }
  }

  const handleSaveProduct = async (productData) => {
    setProcessing(true)
    try {
      const res = await LoansAdminAPI.saveLoanProduct(productData)
      if (res.success) {
        fetchProducts()
        setShowProductModal(false)
        setEditingProduct(null)
      }
    } catch (err) {
      console.error('Failed to save product')
    } finally {
      setProcessing(false)
    }
  }

  const handleDeleteProduct = async (productId) => {
    if (!confirm('Are you sure you want to delete this loan product?')) return

    try {
      const res = await LoansAdminAPI.deleteLoanProduct(productId)
      if (res.success) {
        fetchProducts()
      }
    } catch (err) {
      console.error('Failed to delete product')
    }
  }

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount || 0)
  }

  const formatDate = (date) => {
    return new Date(date).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    })
  }

  const getStatusBadge = (status) => {
    const styles = {
      draft: 'bg-slate-500/20 text-slate-400',
      submitted: 'bg-yellow-500/20 text-yellow-400',
      in_review: 'bg-orange-500/20 text-orange-400',
      approved: 'bg-emerald-500/20 text-emerald-400',
      rejected: 'bg-red-500/20 text-red-400',
      funded: 'bg-blue-500/20 text-blue-400',
      withdrawn: 'bg-slate-500/20 text-slate-400',
      active: 'bg-blue-500/20 text-blue-400',
      paid_off: 'bg-violet-500/20 text-violet-400',
      defaulted: 'bg-red-500/20 text-red-400'
    }
    return styles[status] || 'bg-slate-500/20 text-slate-400'
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-white">Loans Management</h1>
          <p className="text-slate-400 mt-1">Manage loan applications and products</p>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 border-b border-slate-700 pb-2">
        <button
          onClick={() => { setActiveTab('applications'); setPage(1) }}
          className={`px-4 py-2 rounded-lg transition-colors ${
            activeTab === 'applications'
              ? 'bg-blue-500 text-white'
              : 'text-slate-400 hover:bg-slate-700'
          }`}
        >
          Applications
        </button>
        <button
          onClick={() => { setActiveTab('products'); setPage(1) }}
          className={`px-4 py-2 rounded-lg transition-colors ${
            activeTab === 'products'
              ? 'bg-blue-500 text-white'
              : 'text-slate-400 hover:bg-slate-700'
          }`}
        >
          Loan Products
        </button>
      </div>

      {/* Applications Tab */}
      {activeTab === 'applications' && (
        <>
          {/* Filters */}
          <div className="bg-slate-800 border border-slate-700 rounded-xl p-4">
            <div className="flex flex-col md:flex-row gap-4">
              <div className="flex-1 relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-500" />
                <input
                  type="text"
                  placeholder="Search by name, email..."
                  value={search}
                  onChange={(e) => { setSearch(e.target.value); setPage(1) }}
                  className="w-full pl-10 pr-4 py-2.5 bg-slate-900/50 border border-slate-600 rounded-lg text-white placeholder-slate-500 focus:outline-none focus:border-blue-500"
                />
              </div>

              <select
                value={statusFilter}
                onChange={(e) => { setStatusFilter(e.target.value); setPage(1) }}
                className="px-4 py-2.5 bg-slate-900/50 border border-slate-600 rounded-lg text-white focus:outline-none focus:border-blue-500"
              >
                <option value="">All Status</option>
                <option value="draft">Draft</option>
                <option value="submitted">Submitted</option>
                <option value="in_review">In Review</option>
                <option value="approved">Approved</option>
                <option value="rejected">Rejected</option>
                <option value="funded">Funded</option>
                <option value="withdrawn">Withdrawn</option>
              </select>

              <button
                onClick={fetchApplications}
                className="px-4 py-2.5 bg-slate-700 hover:bg-slate-600 text-white rounded-lg transition-colors flex items-center gap-2"
              >
                <RefreshCw className="w-4 h-4" />
                Refresh
              </button>
            </div>
          </div>

          {/* Applications Table */}
          <div className="bg-slate-800 border border-slate-700 rounded-xl overflow-hidden">
            {loadingApps ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-8 h-8 text-blue-500 animate-spin" />
              </div>
            ) : error ? (
              <div className="flex items-center justify-center py-12">
                <div className="text-center">
                  <AlertCircle className="w-10 h-10 text-red-500 mx-auto mb-2" />
                  <p className="text-slate-400">{error}</p>
                </div>
              </div>
            ) : (
              <>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-slate-700 bg-slate-900/50">
                        <th className="text-left py-4 px-4 text-sm font-medium text-slate-400">Applicant</th>
                        <th className="text-left py-4 px-4 text-sm font-medium text-slate-400">Type</th>
                        <th className="text-right py-4 px-4 text-sm font-medium text-slate-400">Amount</th>
                        <th className="text-left py-4 px-4 text-sm font-medium text-slate-400">Term</th>
                        <th className="text-left py-4 px-4 text-sm font-medium text-slate-400">Rate</th>
                        <th className="text-left py-4 px-4 text-sm font-medium text-slate-400">Status</th>
                        <th className="text-left py-4 px-4 text-sm font-medium text-slate-400">Applied</th>
                        <th className="text-center py-4 px-4 text-sm font-medium text-slate-400">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {applications.map((loan) => (
                        <tr key={loan.id} className="border-b border-slate-700/50 hover:bg-slate-700/30 transition-colors">
                          <td className="py-4 px-4">
                            <div className="flex items-center gap-3">
                              <div className="w-10 h-10 bg-slate-700 rounded-full flex items-center justify-center">
                                <User className="w-5 h-5 text-slate-400" />
                              </div>
                              <div>
                                <p className="text-white font-medium">{loan.first_name} {loan.last_name}</p>
                                <p className="text-slate-500 text-sm">{loan.email}</p>
                              </div>
                            </div>
                          </td>
                          <td className="py-4 px-4">
                            <span className="text-white capitalize">{loan.loan_type?.replace('_', ' ')}</span>
                          </td>
                          <td className="py-4 px-4 text-right">
                            <span className="text-white font-semibold">{formatCurrency(loan.amount)}</span>
                          </td>
                          <td className="py-4 px-4 text-slate-400">
                            {loan.term_months} months
                          </td>
                          <td className="py-4 px-4 text-slate-400">
                            {loan.interest_rate}%
                          </td>
                          <td className="py-4 px-4">
                            <span className={`px-2 py-1 rounded text-xs font-medium capitalize ${getStatusBadge(loan.status)}`}>
                              {loan.status}
                            </span>
                          </td>
                          <td className="py-4 px-4 text-slate-400 text-sm">
                            {formatDate(loan.created_at)}
                          </td>
                          <td className="py-4 px-4">
                            <div className="flex items-center justify-center gap-2">
                              <button
                                onClick={() => handleViewLoan(loan)}
                                className="p-2 hover:bg-slate-600 rounded-lg transition-colors"
                                title="View Details"
                              >
                                <Eye className="w-4 h-4 text-slate-400" />
                              </button>
                              {hasPermission('loans.approve') && ['submitted', 'in_review', 'draft', 'approved'].includes(loan.status) && (
                                <>
                                  <button
                                    onClick={() => handleApproveLoan(loan.id)}
                                    className="p-2 hover:bg-emerald-500/20 rounded-lg transition-colors"
                                    title="Approve"
                                  >
                                    <CheckCircle className="w-4 h-4 text-emerald-400" />
                                  </button>
                                  <button
                                    onClick={() => {
                                      setSelectedLoan(loan)
                                      setShowDetailModal(true)
                                    }}
                                    className="p-2 hover:bg-red-500/20 rounded-lg transition-colors"
                                    title="Reject"
                                  >
                                    <XCircle className="w-4 h-4 text-red-400" />
                                  </button>
                                </>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* Pagination */}
                {totalPages > 1 && (
                  <div className="flex items-center justify-between px-4 py-3 border-t border-slate-700">
                    <p className="text-sm text-slate-400">
                      Page {page} of {totalPages} ({total} total)
                    </p>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => setPage(p => Math.max(1, p - 1))}
                        disabled={page === 1}
                        className="p-2 hover:bg-slate-700 rounded-lg transition-colors disabled:opacity-50"
                      >
                        <ChevronLeft className="w-5 h-5 text-slate-400" />
                      </button>
                      <button
                        onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                        disabled={page === totalPages}
                        className="p-2 hover:bg-slate-700 rounded-lg transition-colors disabled:opacity-50"
                      >
                        <ChevronRight className="w-5 h-5 text-slate-400" />
                      </button>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        </>
      )}

      {/* Products Tab */}
      {activeTab === 'products' && (
        <>
          {/* Header */}
          <div className="flex justify-between items-center">
            <p className="text-slate-400">Configure available loan products</p>
            {hasPermission('loans.edit') && (
              <button
                onClick={() => {
                  setEditingProduct(null)
                  setShowProductModal(true)
                }}
                className="flex items-center gap-2 px-4 py-2 bg-blue-500 hover:bg-blue-600 text-white rounded-lg transition-colors"
              >
                <Plus className="w-4 h-4" />
                Add Product
              </button>
            )}
          </div>

          {/* Products Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {loadingProducts ? (
              <div className="col-span-full flex items-center justify-center py-12">
                <Loader2 className="w-8 h-8 text-blue-500 animate-spin" />
              </div>
            ) : products.length === 0 ? (
              <div className="col-span-full text-center py-12 text-slate-400">
                No loan products configured
              </div>
            ) : (
              products.map((product) => (
                <motion.div
                  key={product.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="bg-slate-800 border border-slate-700 rounded-xl p-5"
                >
                  <div className="flex items-start justify-between mb-4">
                    <div className="w-12 h-12 bg-orange-500/20 rounded-xl flex items-center justify-center">
                      <Landmark className="w-6 h-6 text-orange-400" />
                    </div>
                    <span className={`px-2 py-1 rounded text-xs font-medium ${
                      product.is_active ? 'bg-emerald-500/20 text-emerald-400' : 'bg-slate-500/20 text-slate-400'
                    }`}>
                      {product.is_active ? 'Active' : 'Inactive'}
                    </span>
                  </div>

                  <h3 className="text-lg font-semibold text-white mb-2">{product.name}</h3>
                  <p className="text-slate-400 text-sm mb-4 line-clamp-2">{product.description}</p>

                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-slate-400">Interest Rate</span>
                      <span className="text-white font-medium">{product.min_rate}% - {product.max_rate}%</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-400">Amount Range</span>
                      <span className="text-white font-medium">
                        {formatCurrency(product.min_amount)} - {formatCurrency(product.max_amount)}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-400">Term</span>
                      <span className="text-white font-medium">{product.min_term} - {product.max_term} months</span>
                    </div>
                  </div>

                  {hasPermission('loans.edit') && (
                    <div className="flex gap-2 mt-4 pt-4 border-t border-slate-700">
                      <button
                        onClick={() => {
                          setEditingProduct(product)
                          setShowProductModal(true)
                        }}
                        className="flex-1 flex items-center justify-center gap-2 px-3 py-2 bg-slate-700 hover:bg-slate-600 text-white rounded-lg transition-colors text-sm"
                      >
                        <Edit2 className="w-4 h-4" />
                        Edit
                      </button>
                      <button
                        onClick={() => handleDeleteProduct(product.id)}
                        className="px-3 py-2 bg-red-500/20 hover:bg-red-500/30 text-red-400 rounded-lg transition-colors"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  )}
                </motion.div>
              ))
            )}
          </div>
        </>
      )}

      {/* Loan Detail Modal */}
      <AnimatePresence>
        {showDetailModal && selectedLoan && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
            onClick={() => setShowDetailModal(false)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="bg-slate-800 border border-slate-700 rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto"
              onClick={e => e.stopPropagation()}
            >
              <div className="p-6 border-b border-slate-700 flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-gradient-to-br from-orange-500 to-amber-600 rounded-xl flex items-center justify-center">
                    <Landmark className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h2 className="text-xl font-bold text-white">Loan Application</h2>
                    <p className="text-slate-400 capitalize">{selectedLoan.loan_type?.replace('_', ' ')}</p>
                  </div>
                </div>
                <button
                  onClick={() => setShowDetailModal(false)}
                  className="p-2 hover:bg-slate-700 rounded-lg transition-colors"
                >
                  <X className="w-5 h-5 text-slate-400" />
                </button>
              </div>

              <div className="p-6 space-y-6">
                {/* Amount Card */}
                <div className="bg-gradient-to-br from-orange-500/20 to-amber-500/20 border border-orange-500/30 rounded-xl p-6">
                  <p className="text-slate-400 text-sm mb-2">Requested Amount</p>
                  <p className="text-4xl font-bold text-white">{formatCurrency(selectedLoan.amount)}</p>
                  <div className="mt-4 flex items-center gap-4">
                    <span className={`px-3 py-1 rounded-lg text-sm font-medium capitalize ${getStatusBadge(selectedLoan.status)}`}>
                      {selectedLoan.status}
                    </span>
                  </div>
                </div>

                {/* Loan Details */}
                <div className="grid grid-cols-2 gap-4">
                  <div className="bg-slate-900/50 rounded-lg p-4">
                    <div className="flex items-center gap-2 text-slate-400 text-sm mb-1">
                      <User className="w-4 h-4" />
                      Applicant
                    </div>
                    <p className="text-white">{selectedLoan.first_name} {selectedLoan.last_name}</p>
                    <p className="text-slate-500 text-sm">{selectedLoan.email}</p>
                  </div>
                  <div className="bg-slate-900/50 rounded-lg p-4">
                    <div className="flex items-center gap-2 text-slate-400 text-sm mb-1">
                      <Percent className="w-4 h-4" />
                      Interest Rate
                    </div>
                    <p className="text-white">{selectedLoan.interest_rate}% APR</p>
                  </div>
                  <div className="bg-slate-900/50 rounded-lg p-4">
                    <div className="flex items-center gap-2 text-slate-400 text-sm mb-1">
                      <Clock className="w-4 h-4" />
                      Term
                    </div>
                    <p className="text-white">{selectedLoan.term_months} months</p>
                  </div>
                  <div className="bg-slate-900/50 rounded-lg p-4">
                    <div className="flex items-center gap-2 text-slate-400 text-sm mb-1">
                      <DollarSign className="w-4 h-4" />
                      Monthly Payment
                    </div>
                    <p className="text-white">{formatCurrency(selectedLoan.monthly_payment)}</p>
                  </div>
                  <div className="bg-slate-900/50 rounded-lg p-4">
                    <div className="flex items-center gap-2 text-slate-400 text-sm mb-1">
                      <Calendar className="w-4 h-4" />
                      Applied Date
                    </div>
                    <p className="text-white">{formatDate(selectedLoan.created_at)}</p>
                  </div>
                  <div className="bg-slate-900/50 rounded-lg p-4">
                    <div className="flex items-center gap-2 text-slate-400 text-sm mb-1">
                      <FileText className="w-4 h-4" />
                      Purpose
                    </div>
                    <p className="text-white">{selectedLoan.purpose || 'Not specified'}</p>
                  </div>
                </div>

                {/* Rejection Reason Input (for reviewable loans) */}
                {hasPermission('loans.approve') && ['submitted', 'in_review', 'draft', 'approved'].includes(selectedLoan.status) && (
                  <div className="space-y-4 pt-4 border-t border-slate-700">
                    <h3 className="text-lg font-semibold text-white">Review Application</h3>

                    <div>
                      <label className="block text-sm font-medium text-slate-300 mb-2">
                        Rejection Reason (required for rejection)
                      </label>
                      <textarea
                        value={rejectionReason}
                        onChange={(e) => setRejectionReason(e.target.value)}
                        placeholder="Provide a reason if rejecting..."
                        rows={3}
                        className="w-full px-4 py-3 bg-slate-900/50 border border-slate-600 rounded-lg text-white placeholder-slate-500 focus:outline-none focus:border-blue-500 resize-none"
                      />
                    </div>

                    <div className="flex gap-3">
                      <button
                        onClick={() => handleApproveLoan(selectedLoan.id)}
                        disabled={processing}
                        className="flex-1 flex items-center justify-center gap-2 px-4 py-3 bg-emerald-500 hover:bg-emerald-600 text-white rounded-lg transition-colors disabled:opacity-50"
                      >
                        {processing ? (
                          <Loader2 className="w-5 h-5 animate-spin" />
                        ) : (
                          <>
                            <CheckCircle className="w-5 h-5" />
                            Approve Loan
                          </>
                        )}
                      </button>
                      <button
                        onClick={() => handleRejectLoan(selectedLoan.id)}
                        disabled={processing || !rejectionReason}
                        className="flex-1 flex items-center justify-center gap-2 px-4 py-3 bg-red-500 hover:bg-red-600 text-white rounded-lg transition-colors disabled:opacity-50"
                      >
                        {processing ? (
                          <Loader2 className="w-5 h-5 animate-spin" />
                        ) : (
                          <>
                            <XCircle className="w-5 h-5" />
                            Reject Loan
                          </>
                        )}
                      </button>
                    </div>
                  </div>
                )}

                {/* Show rejection reason if rejected */}
                {selectedLoan.status === 'rejected' && selectedLoan.rejection_reason && (
                  <div className="p-4 bg-red-500/10 border border-red-500/30 rounded-lg">
                    <p className="text-red-400 text-sm font-medium mb-1">Rejection Reason:</p>
                    <p className="text-white">{selectedLoan.rejection_reason}</p>
                  </div>
                )}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Product Modal */}
      <AnimatePresence>
        {showProductModal && (
          <ProductFormModal
            product={editingProduct}
            onClose={() => {
              setShowProductModal(false)
              setEditingProduct(null)
            }}
            onSave={handleSaveProduct}
            processing={processing}
          />
        )}
      </AnimatePresence>
    </div>
  )
}

// Product Form Modal Component
function ProductFormModal({ product, onClose, onSave, processing }) {
  const getInitialFormData = (p) => ({
    name: p?.name || '',
    description: p?.description || '',
    loan_type: p?.loan_type || 'personal',
    min_amount: p?.min_amount || 1000,
    max_amount: p?.max_amount || 50000,
    min_rate: p?.base_apr || p?.min_rate || 5,
    max_rate: p?.max_apr || p?.max_rate || 15,
    min_term: p?.min_term_months || p?.min_term || 6,
    max_term: p?.max_term_months || p?.max_term || 60,
    is_active: p?.is_active === 1 || p?.is_active === true ? true : (p?.is_active === 0 ? false : true)
  })

  const [formData, setFormData] = useState(getInitialFormData(product))

  const handleSubmit = (e) => {
    e.preventDefault()
    onSave({
      ...formData,
      id: product?.id
    })
  }

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.95, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.95, opacity: 0 }}
        className="bg-slate-800 border border-slate-700 rounded-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto"
        onClick={e => e.stopPropagation()}
      >
        <form onSubmit={handleSubmit}>
          <div className="p-6 border-b border-slate-700">
            <h2 className="text-xl font-bold text-white">
              {product ? 'Edit Loan Product' : 'New Loan Product'}
            </h2>
          </div>

          <div className="p-6 space-y-4">
            <div>
              <label className="block text-sm font-medium text-slate-300 mb-2">Product Name</label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className="w-full px-4 py-3 bg-slate-900/50 border border-slate-600 rounded-lg text-white focus:outline-none focus:border-blue-500"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-300 mb-2">Description</label>
              <textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                rows={3}
                className="w-full px-4 py-3 bg-slate-900/50 border border-slate-600 rounded-lg text-white focus:outline-none focus:border-blue-500 resize-none"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-300 mb-2">Loan Type</label>
              <select
                value={formData.loan_type}
                onChange={(e) => setFormData({ ...formData, loan_type: e.target.value })}
                className="w-full px-4 py-3 bg-slate-900/50 border border-slate-600 rounded-lg text-white focus:outline-none focus:border-blue-500"
              >
                <option value="personal">Personal</option>
                <option value="auto">Auto</option>
                <option value="mortgage">Mortgage</option>
                <option value="business">Business</option>
                <option value="student">Student</option>
              </select>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">Min Amount</label>
                <input
                  type="number"
                  value={formData.min_amount}
                  onChange={(e) => setFormData({ ...formData, min_amount: parseFloat(e.target.value) })}
                  className="w-full px-4 py-3 bg-slate-900/50 border border-slate-600 rounded-lg text-white focus:outline-none focus:border-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">Max Amount</label>
                <input
                  type="number"
                  value={formData.max_amount}
                  onChange={(e) => setFormData({ ...formData, max_amount: parseFloat(e.target.value) })}
                  className="w-full px-4 py-3 bg-slate-900/50 border border-slate-600 rounded-lg text-white focus:outline-none focus:border-blue-500"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">Min Rate (%)</label>
                <input
                  type="number"
                  step="0.1"
                  value={formData.min_rate}
                  onChange={(e) => setFormData({ ...formData, min_rate: parseFloat(e.target.value) })}
                  className="w-full px-4 py-3 bg-slate-900/50 border border-slate-600 rounded-lg text-white focus:outline-none focus:border-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">Max Rate (%)</label>
                <input
                  type="number"
                  step="0.1"
                  value={formData.max_rate}
                  onChange={(e) => setFormData({ ...formData, max_rate: parseFloat(e.target.value) })}
                  className="w-full px-4 py-3 bg-slate-900/50 border border-slate-600 rounded-lg text-white focus:outline-none focus:border-blue-500"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">Min Term (months)</label>
                <input
                  type="number"
                  value={formData.min_term}
                  onChange={(e) => setFormData({ ...formData, min_term: parseInt(e.target.value) })}
                  className="w-full px-4 py-3 bg-slate-900/50 border border-slate-600 rounded-lg text-white focus:outline-none focus:border-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">Max Term (months)</label>
                <input
                  type="number"
                  value={formData.max_term}
                  onChange={(e) => setFormData({ ...formData, max_term: parseInt(e.target.value) })}
                  className="w-full px-4 py-3 bg-slate-900/50 border border-slate-600 rounded-lg text-white focus:outline-none focus:border-blue-500"
                />
              </div>
            </div>

            <div className="flex items-center gap-3">
              <input
                type="checkbox"
                id="is_active"
                checked={formData.is_active}
                onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                className="w-5 h-5 bg-slate-900/50 border border-slate-600 rounded"
              />
              <label htmlFor="is_active" className="text-slate-300">Active</label>
            </div>
          </div>

          <div className="p-6 border-t border-slate-700 flex gap-3">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-3 bg-slate-700 hover:bg-slate-600 text-white rounded-lg transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={processing}
              className="flex-1 flex items-center justify-center gap-2 px-4 py-3 bg-blue-500 hover:bg-blue-600 text-white rounded-lg transition-colors disabled:opacity-50"
            >
              {processing ? (
                <Loader2 className="w-5 h-5 animate-spin" />
              ) : (
                <>
                  <Save className="w-5 h-5" />
                  Save Product
                </>
              )}
            </button>
          </div>
        </form>
      </motion.div>
    </motion.div>
  )
}
